<script setup lang="ts">
import {computed, type Component} from "vue";
import {HandHeart, Moon, Sun, Sunrise, Sunset} from "@lucide/vue";
import {DosageFormData} from "@/dosage/interfaces/DosageForm";
import PosologyGlance, {GLANCE_SLOTS, GlanceSlot} from "@/medication-checkup/services/PosologyGlance";
import DoseInputService from "@/dosage/services/DoseInputService";

/**
 * A posology drawn to be read at a glance: the dose at each moment of the
 * day, the days of the week it falls on, or a take on demand. A rhythm the
 * glance cannot draw draws nothing — the words beside it say it.
 */

const props = defineProps<{
    data: DosageFormData;
}>();

const glance = computed(() => PosologyGlance.of(props.data));

const SLOTS: Record<GlanceSlot, {label: string; icon: Component}> = {
    morning: {label: 'Matin', icon: Sunrise},
    noon: {label: 'Midi', icon: Sun},
    evening: {label: 'Soir', icon: Sunset},
    bedtime: {label: 'Coucher', icon: Moon}
};

/**
 * The days of the week from Monday, as the referential numbers them —
 * Sunday being 0.
 */
const WEEK = [
    {day: 1, label: 'L'},
    {day: 2, label: 'M'},
    {day: 3, label: 'M'},
    {day: 4, label: 'J'},
    {day: 5, label: 'V'},
    {day: 6, label: 'S'},
    {day: 0, label: 'D'}
];
</script>

<template>
  <span v-if="glance.kind === 'grid'" class="flex shrink-0 flex-col items-end gap-1">
    <span class="grid grid-cols-[repeat(4,38px)] gap-[3px]">
      <span
        v-for="slot in GLANCE_SLOTS"
        :key="slot"
        :title="SLOTS[slot].label"
        class="flex flex-col items-center gap-px rounded-[7px] py-[3px]"
        :class="glance.doses[slot] > 0 ? 'bg-lightPurple-lightest text-primary' : 'bg-gray-light text-gray'"
      >
        <component :is="SLOTS[slot].icon" class="size-3 opacity-85" />
        <span class="text-[12px] font-bold leading-none">
          {{ glance.doses[slot] > 0 ? DoseInputService.convertToFraction(glance.doses[slot]) : '–' }}
        </span>
      </span>
    </span>
    <span v-if="glance.days" class="flex w-full gap-0.5">
      <span
        v-for="day in WEEK"
        :key="day.day"
        class="flex-1 rounded py-px text-center text-[9.5px] font-bold"
        :class="glance.days.includes(day.day) ? 'bg-primary text-white' : 'bg-gray-light text-gray-dark/60'"
      >
        {{ day.label }}
      </span>
    </span>
    <span v-else-if="glance.rhythm" class="rounded-full bg-lightPurple-lightest px-2 py-px text-[10px] font-semibold text-primary">
      {{ glance.rhythm }}
    </span>
  </span>

  <span
    v-else-if="glance.kind === 'needed'"
    class="flex w-[161px] shrink-0 items-center justify-center gap-1.5 rounded-[7px] bg-orange/20 py-1.5 text-[11px] font-bold text-amber-800"
  >
    <HandHeart class="size-3.5" />
    {{ glance.label }}
  </span>
</template>
